// What a shell hands the AI Companion's chat (rep-app W6, spec section 9
// "Embedding"): how a request travels, where a link goes, what is on screen,
// where history lives, whether the device is online, and how a client action
// is applied. The ERP passes erpHost.tsx (router, zoom breadcrumb, cookie
// client, localStorage); the Sales app passes web/src/rep/companion's host
// (its router, the device token, IndexedDB meta, the local cart). Types and
// one guard only: nothing here may import the ERP's router or zoom store,
// which the Sales app bundle must not reach (rep/__tests__/entryReach.test.ts).
import type { ReactNode } from 'react';
import type { LinePriceSource } from '@/shared/pricing/types';

export type CopilotQuery = Record<string, string | number | undefined>;

// Paths are relative to /api/v1; the answer is the unwrapped `data`; a refusal
// throws ApiError (shared/api/client.ts), a lost connection a TypeError.
export type CopilotRequest = {
  get<T = unknown>(path: string, query?: CopilotQuery): Promise<T>;
  post<T = unknown>(path: string, body?: unknown): Promise<T>;
};

export type ProLine = {
  description?: string; qty?: number; unit_price?: number | null; tax_rate?: number; item_id?: string | null; revenue_account_id?: string | null;
  // Price authority preview (rep-app W2): while price_auto is true and the
  // line still shows auto_unit_price for auto_item_id, the executor sends it
  // WITHOUT unit_price and the server prices it at the real qty; any other
  // price is a typed price (price_override rules). price_source says where
  // the shown price came from.
  price_auto?: boolean; auto_unit_price?: number | null; auto_item_id?: string | null; price_source?: LinePriceSource | null;
  // Sales tax (a sales card): the line's rate for a taxable item, in its
  // tax_rate's unit. tax_rate is 0 while the line's item is not taxable; an
  // item picked on the line puts this back when it is (services/item-tax.js).
  taxable_rate?: number;
};
export type ProPayload = { date?: string; due_date?: string; memo?: string; currency_code?: string; lines?: ProLine[]; [k: string]: unknown };

// An ERP draft proposal (services/ai-actions): approved through
// POST /ai/actions/execute.
export type ProposalT = {
  kind?: undefined;
  action: string; title?: string; summary?: string; editable?: boolean;
  // Optional label overrides for the two editable date fields, so the same
  // line card serves invoices ("Invoice date"/"Due date"), bills, sales
  // orders ("Order date"/"Requested date"), quotes, etc.
  labels?: { date?: string; due?: string };
  // When set, the proposal is NOT a create-a-draft action: the primary button
  // opens an existing app form (e.g. Report Production) where the user reviews
  // and posts. Used for flows that can't be safely auto-posted on one click.
  formLink?: string;
  meta?: { customer?: string; currency?: string; terms?: string; functional?: string };
  display?: Record<string, string | string[]>;
  payload: ProPayload;
};

// A client action (decision D19): the Sales app's cart proposal, applied on
// the device by host.applyClientAction; nothing is created on the server.
export type ClientActionLine = { item_id: string; code: string; name: string; qty: string };
export type ClientActionProposal = {
  kind: 'client_action'; action: 'cart_lines'; title?: string; summary?: string;
  customer: { id: string; code?: string; name: string };
  lines: ClientActionLine[];
};

// A follow-up email the rep reviews and sends from their mail app.
export type EmailDraft = { customer?: { id: string; name: string }; so_id?: string | null; to: string; subject: string; body: string };

export type ChoiceCandidate = { id: string; code: string; name: string; price?: number | null; currency?: string; note?: string | null };
export type ChoiceT = { kind?: 'item' | 'customer' | 'vendor'; query?: string; note?: string; candidates: ChoiceCandidate[] };

export type CopilotMsg = {
  role: 'user' | 'assistant';
  content: string;
  proposal?: ProposalT | ClientActionProposal; // pending one-click approval
  choice?: ChoiceT;                // "pick an item" card
  choiceResolved?: string;         // label of the picked item (card collapses)
  links?: { label: string; path: string }[]; // clickable in-app nav targets
  result?: { label: string; link?: string; kind?: 'draft' | 'cart' | 'customer' }; // executed draft, created customer or applied cart lines
  draft?: EmailDraft;              // follow-up email card
  actionErr?: string;
  approving?: boolean;
};

// load() may answer at once (localStorage) or later (IndexedDB); the chat
// takes no input and saves nothing until it has answered.
export type CopilotHistory = {
  load(): CopilotMsg[] | null | Promise<CopilotMsg[] | null>;
  save(messages: CopilotMsg[]): void;
  clear(): void;
};

export type CopilotHost = {
  request: CopilotRequest;
  navigate(path: string): void;
  // Sent with every question: the screen's label, the record and path on
  // screen, and any extra fields the shell's server mode reads (the Sales
  // app's customer_id, so_id, cart_item_ids).
  context: { label?: string; record?: string | null; recordPath?: string; extra?: Record<string, unknown> };
  history: CopilotHistory;
  online?: boolean;                 // false: no input, offlineNotice shows
  offlineNotice?: ReactNode;
  copy?: { empty?: string; placeholder?: string; suggestions?: string[] };
  showLinks?: boolean;              // the ERP's navigation links (default true)
  applyClientAction?(p: ClientActionProposal): Promise<{ label: string; path?: string }>;
  onExecuted?(): void;              // after an ERP draft was created
};

export const isClientAction = (p: CopilotMsg['proposal']): p is ClientActionProposal =>
  !!p && (p as { kind?: string }).kind === 'client_action';
